import React, { useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Download, TrendingUp, BarChart3 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { format, parseISO } from 'date-fns';

interface ChipChipSalesData {
  id: string;
  product_name: string;
  date: string;
  order_type: string;
  price: number;
  num_order: number;
  quantity: number;
  sync_date: string;
}

interface ChipChipSalesChartProps {
  data: ChipChipSalesData[];
  selectedProduct?: string;
}

interface ChartDataPoint {
  date: string;
  displayDate: string;
  price: number;
  quantity: number;
  orders: number;
  [key: string]: string | number;
}

const ChipChipSalesChart: React.FC<ChipChipSalesChartProps> = ({
  data,
  selectedProduct
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartType, setChartType] = React.useState<'line' | 'bar'>('line');

  // Process data for charts
  const chartData = useMemo(() => {
    if (!data?.length) return [];

    // Filter by product if selected
    const filteredData = selectedProduct 
      ? data.filter(item => item.product_name === selectedProduct)
      : data;

    // Group by date and calculate aggregates
    const dateGroups = filteredData.reduce((acc, item) => {
      const date = item.date;
      if (!acc[date]) {
        acc[date] = {
          totalPrice: 0,
          totalQuantity: 0,
          totalOrders: 0,
          count: 0,
          orderTypes: new Set<string>()
        };
      }
      
      acc[date].totalPrice += item.price;
      acc[date].totalQuantity += item.quantity;
      acc[date].totalOrders += item.num_order;
      acc[date].count += 1;
      acc[date].orderTypes.add(item.order_type);
      
      return acc;
    }, {} as Record<string, any>);

    // Convert to chart format
    return Object.entries(dateGroups)
      .map(([date, stats]) => ({
        date,
        displayDate: format(parseISO(date), 'MMM dd'),
        price: Math.round((stats.totalPrice / stats.count) * 100) / 100,
        quantity: stats.totalQuantity,
        orders: stats.totalOrders,
        orderTypes: Array.from(stats.orderTypes).join(', ')
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [data, selectedProduct]);

  // Get unique products for selection
  const products = useMemo(() => {
    if (!data?.length) return [];
    return Array.from(new Set(data.map(item => item.product_name))).sort();
  }, [data]);

  const downloadChart = async () => {
    if (!chartRef.current) return;

    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: 'white',
        scale: 2,
      });
      
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `chipchip-sales-chart-${format(new Date(), 'yyyy-MM-dd')}.png`;
      a.click();
    } catch (error) {
      console.error('Failed to download chart:', error);
    }
  };

  const formatTooltip = (value: number, name: string) => {
    switch (name) {
      case 'price':
        return [`${value} ETB`, 'Average Price'];
      case 'quantity':
        return [`${value.toLocaleString()}`, 'Total Quantity'];
      case 'orders':
        return [`${value.toLocaleString()}`, 'Total Orders'];
      default:
        return [value, name];
    }
  };

  if (!data?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            ChipChip Sales Trends
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">No sales data available for chart visualization</p>
        </CardContent>
      </Card>
    );
  }

  if (!chartData?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            ChipChip Sales Trends
          </CardTitle>
          {selectedProduct && (
            <p className="text-sm text-muted-foreground mt-1">
              Showing data for: {selectedProduct}
            </p>
          )}
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">
            {selectedProduct ? `No data available for ${selectedProduct}` : 'No data available for the selected date range'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            <div>
              <CardTitle>ChipChip Sales Trends</CardTitle>
              {selectedProduct && (
                <p className="text-sm text-muted-foreground mt-1">
                  Showing data for: {selectedProduct}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setChartType(chartType === 'line' ? 'bar' : 'line')}
            >
              {chartType === 'line' ? <BarChart3 className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
              {chartType === 'line' ? 'Bar Chart' : 'Line Chart'}
            </Button>
            <Button onClick={downloadChart} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div ref={chartRef} className="space-y-6">
          {/* Price Chart */}
          <div>
            <h3 className="text-sm font-medium mb-3">Average Price Trends</h3>
            <ChartContainer
              config={{
                price: {
                  label: "Price (ETB)",
                  color: "hsl(var(--chart-1))",
                },
              }}
              className="h-64"
            >
              {chartType === 'line' ? (
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="displayDate"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value} ETB`}
                  />
                  <ChartTooltip
                    content={<ChartTooltipContent formatter={formatTooltip} />}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-1))", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="displayDate"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${value} ETB`}
                  />
                  <ChartTooltip
                    content={<ChartTooltipContent formatter={formatTooltip} />}
                  />
                  <Bar
                    dataKey="price"
                    fill="hsl(var(--chart-1))"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              )}
            </ChartContainer>
          </div>

          {/* Volume Chart */}
          <div>
            <h3 className="text-sm font-medium mb-3">Quantity & Orders Volume</h3>
            <ChartContainer
              config={{
                quantity: {
                  label: "Quantity",
                  color: "hsl(var(--chart-2))",
                },
                orders: {
                  label: "Orders",
                  color: "hsl(var(--chart-3))",
                },
              }}
              className="h-64"
            >
              {chartType === 'line' ? (
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="displayDate"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <ChartTooltip
                    content={<ChartTooltipContent formatter={formatTooltip} />}
                  />
                  <Line
                    type="monotone"
                    dataKey="quantity"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-2))", strokeWidth: 2, r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="orders"
                    stroke="hsl(var(--chart-3))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-3))", strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="displayDate"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.toLocaleString()}
                  />
                  <ChartTooltip
                    content={<ChartTooltipContent formatter={formatTooltip} />}
                  />
                  <Bar
                    dataKey="quantity"
                    fill="hsl(var(--chart-2))"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="orders"
                    fill="hsl(var(--chart-3))"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              )}
            </ChartContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChipChipSalesChart;